import { Box, Skeleton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { ACCENTS, CARD_SX, COLORS, rise } from "./emsTheme";
import { gradientBtnSx } from "./SectionCard";

// Small building blocks shared by the EMS V2 settings tabs (Categories, Auto Tagging & Email
// Config, Report Schedules). Colours come from emsTheme; nothing here is smaller than 12px.

export const OFF_GREY = { solid: "#6B7280", soft: "#F3F4F6", grad: "linear-gradient(135deg, #C4C8CF 0%, #9AA0AA 100%)" };
export const accentOf = (key) => ACCENTS[key] || ACCENTS.purple;

/** A card with a 4px coloured bar along its top edge. */
export const topBarCardSx = (accent = ACCENTS.purple) => ({
    ...CARD_SX, p: 0, position: "relative", overflow: "hidden", minWidth: 0,
    "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: 4, background: accent.grad },
});

/** Purple instead of the admin app's blue for switches, radios, checkboxes and focused inputs. */
export const purpleControlsSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.purple },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: COLORS.purple, opacity: 0.5 },
    "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": { color: COLORS.purple },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.purple },
    "& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-root.Mui-focused": { color: COLORS.purple },
};

/** Plain text button in the EMS purple. */
export const textBtnSx = {
    textTransform: "none", fontWeight: 700, color: COLORS.purple, borderRadius: "8px",
    "&:hover": { bgcolor: COLORS.purpleSoft },
};
export const cancelBtnSx = { textTransform: "none", fontWeight: 600, color: COLORS.ink2, borderRadius: "8px" };

/** Gradient icon tile. */
export const IconTile = ({ icon: Icon, accent = ACCENTS.purple, size = 34 }) => (
    <Box aria-hidden sx={{ width: size, height: size, borderRadius: "10px", background: accent.grad, display: "grid", placeItems: "center",
        flexShrink: 0, boxShadow: `0 4px 10px ${accent.solid}33` }}>
        {Icon && <Icon sx={{ fontSize: Math.round(size * 0.55), color: "#fff" }} />}
    </Box>
);

/** Uppercase coloured label above a title. */
export const Eyebrow = ({ children, color = COLORS.purple, sx }) => (
    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color, ...sx }}>{children}</Typography>
);

/** Soft pill: tinted background, solid text. */
export const Pill = ({ children, accent = ACCENTS.purple, icon: Icon, sx, title }) => (
    <Box component="span" title={title} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.25, borderRadius: "999px",
        bgcolor: accent.soft, color: accent.solid, fontSize: 12, fontWeight: 700, lineHeight: 1.6, whiteSpace: "nowrap", maxWidth: "100%", ...sx }}>
        {Icon && <Icon sx={{ fontSize: 14 }} aria-hidden />}
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{children}</Box>
    </Box>
);

/** "On" / "Off" state pill with a dot, so the state never relies on colour alone. */
export const StatePill = ({ on, onLabel = "On", offLabel = "Off", sx }) => {
    const a = on ? ACCENTS.green : OFF_GREY;
    return (
        <Pill accent={a} sx={sx}>
            <Box component="span" aria-hidden sx={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", bgcolor: a.solid, mr: 0.5, verticalAlign: "middle" }} />
            {on ? onLabel : offLabel}
        </Pill>
    );
};

/** A compact summary number with a coloured top edge and an icon tile. */
export const ConfigStat = ({ icon, label, value, caption, accent = "purple", order = 0, loading }) => {
    const a = accentOf(accent);
    return (
        <Box sx={{ ...topBarCardSx(a), p: { xs: 1.5, sm: 2 }, pt: { xs: 2, sm: 2.25 }, display: "flex", gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, ...rise(order) }}>
            <IconTile icon={icon} accent={a} size={38} />
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.ink3, overflowWrap: "anywhere" }}>{label}</Typography>
                {loading ? <Skeleton width={64} height={30} /> : (
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: COLORS.ink, lineHeight: 1.25, fontVariantNumeric: "tabular-nums",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Typography>
                )}
                {caption && <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>{caption}</Typography>}
            </Box>
        </Box>
    );
};

/** Friendly empty / no-access state: a soft icon disc, a title, a line, and an optional action. */
export const ConfigEmpty = ({ icon: Icon, title, text, action, accent = ACCENTS.purple, children, sx }) => (
    <Box sx={{ border: `1.5px dashed ${COLORS.grid}`, borderRadius: "12px", bgcolor: COLORS.surface, px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 5 },
        textAlign: "center", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", justifyItems: "center", gap: 1, ...rise(1), ...sx }}>
        {Icon && (
            <Box aria-hidden sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: accent.soft, display: "grid", placeItems: "center", mb: 0.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "14px", background: accent.grad, display: "grid", placeItems: "center", boxShadow: `0 6px 16px ${accent.solid}40` }}>
                    <Icon sx={{ fontSize: 24, color: "#fff" }} />
                </Box>
            </Box>
        )}
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: COLORS.ink }}>{title}</Typography>
        {text && <Typography sx={{ fontSize: 14, color: COLORS.ink3, maxWidth: 520 }}>{text}</Typography>}
        {children}
        {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
);

/** Pill-shaped segmented control. options: [{ value, label, count?, accent? }]. */
export const Segmented = ({ value, options, onChange, ariaLabel }) => (
    <Box role="radiogroup" aria-label={ariaLabel} sx={{ display: "inline-flex", p: 0.375, gap: 0.25, borderRadius: "999px", bgcolor: COLORS.ground,
        border: `1px solid ${COLORS.grid}`, maxWidth: "100%", overflowX: "auto" }}>
        {options.map((o) => {
            const on = o.value === value;
            const a = accentOf(o.accent);
            return (
                <Box key={o.value} component="button" type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
                    sx={{ all: "unset", boxSizing: "border-box", cursor: "pointer", px: 1.5, py: 0.5, borderRadius: "999px", fontSize: 13, fontWeight: 700,
                        whiteSpace: "nowrap", color: on ? "#fff" : COLORS.ink2, background: on ? a.grad : "transparent",
                        boxShadow: on ? `0 3px 8px ${a.solid}40` : "none", transition: "background-color 150ms, color 150ms",
                        "&:hover": on ? {} : { color: a.solid, bgcolor: "#fff" },
                        "&:focus-visible": { outline: `2px solid ${a.solid}`, outlineOffset: 1 } }}>
                    {o.label}{o.count != null && <Box component="span" sx={{ ml: 0.75, opacity: 0.8, fontVariantNumeric: "tabular-nums" }}>{o.count}</Box>}
                </Box>
            );
        })}
    </Box>
);

/** Purple "how this works" note above a settings form. */
export const InfoNote = ({ children, sx }) => (
    <Box sx={{ display: "flex", gap: 1.25, bgcolor: COLORS.purpleSoft, border: "1px solid #E2D7EE", borderRadius: "10px", p: 1.75, mb: 2.5, ...sx }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: COLORS.purple, mt: 0.25, flexShrink: 0 }} aria-hidden />
        <Typography component="div" sx={{ fontSize: 13, color: COLORS.ink2, lineHeight: 1.6 }}>{children}</Typography>
    </Box>
);

/** One setting in a settings form: the control, then a plain line saying what it does right now.
 *  `muted` greys the control (e.g. it only applies once the feature is on); tone colours the line. */
export const SettingPanel = ({ muted = false, helper, tone = "neutral", children }) => {
    const toneColor = tone === "on" ? ACCENTS.green.solid : tone === "off" ? COLORS.ink3 : COLORS.ink3;
    const ToneIcon = tone === "on" ? CheckCircleOutlineIcon : tone === "off" ? DoNotDisturbOnOutlinedIcon : InfoOutlinedIcon;
    return (
        <Box sx={{ bgcolor: muted ? COLORS.ground : "#fff", borderRadius: "10px", border: `1px ${muted ? "dashed" : "solid"} ${muted ? "#D9D6E0" : COLORS.grid}`,
            p: 2, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1, transition: "background-color 200ms, border-color 200ms",
            ...purpleControlsSx, "& legend.MuiFormLabel-root": { fontSize: 13, fontWeight: 700, color: COLORS.ink2, mb: 0.25 } }}>
            <Box sx={{ opacity: muted ? 0.6 : 1, transition: "opacity 200ms", minWidth: 0 }}>{children}</Box>
            {helper && (
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", mt: "auto" }}>
                    <ToneIcon sx={{ fontSize: 16, color: toneColor, mt: 0.125, flexShrink: 0 }} aria-hidden />
                    <Typography sx={{ fontSize: 12, color: toneColor, fontWeight: tone === "on" ? 600 : 400, lineHeight: 1.5 }}>{helper}</Typography>
                </Box>
            )}
        </Box>
    );
};

/** Email addresses as small chips (display only). */
export const EmailChips = ({ emails, accent = ACCENTS.purple, max = 0 }) => {
    if (!emails || !emails.length) return null;
    const shown = max ? emails.slice(0, max) : emails;
    const rest = emails.length - shown.length;
    return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", minWidth: 0 }}>
            {shown.map((e) => <Pill key={e} accent={accent} icon={MailOutlineIcon} title={e}>{e}</Pill>)}
            {rest > 0 && <Pill accent={OFF_GREY} title={emails.slice(shown.length).join(", ")}>+{rest} more</Pill>}
        </Box>
    );
};

/** Save button for settings forms, in the EMS purple. */
export const configSaveBtnSx = { ...gradientBtnSx, px: 3 };
